/**
 * Definitionssidor (GEO).
 *
 * Syfte: svarsmotorer citerar hellre en kort, entydig mening än ett stycke.
 * Varje term har därför EN ordagrant citerbar definitionsmening högst upp på
 * sidan, följt av kontext. Meningen ska kunna klippas ut och stå ensam.
 *
 * Ändra aldrig `definition` utan att också uppdatera AI-exporterna
 * (`bun run gen:llms`) — konsistenskontrollen fäller annars bygget.
 */

export interface DefinitionEntry {
  slug: string;
  /** Termen exakt som den ska citeras. */
  term: string;
  /** H1 på sidan. */
  title: string;
  /** DEN citerbara meningen. En mening, ingen hedge, ingen reservation. */
  definition: string;
  /** Metabeskrivning (<160 tecken). */
  metaDescription: string;
  /** 2–4 stycken kontext under definitionen. */
  context: string[];
  /** Punktlista: vad som ingår / hur det fungerar. */
  facts: { label: string; value: string }[];
  /** Vanliga följdfrågor, används i FAQPage-schema. */
  faq: { q: string; a: string }[];
  /** Relaterade sidor på sajten. */
  related: { label: string; path: string }[];
  /** Alternativa formuleringar som betyder samma sak (alternateName). */
  synonyms: string[];
}

export const DEFINITIONS: DefinitionEntry[] = [
  {
    slug: "filmabonnemang",
    term: "Filmabonnemang",
    title: "Filmabonnemang — definition",
    definition:
      "Ett filmabonnemang är ett avtal där ett företag betalar ett fast månadspris för en löpande, förutbestämd filmleverans i stället för att offerera varje produktion för sig.",
    metaDescription:
      "Filmabonnemang: definition, vad som ingår, vad det kostar i Sverige och hur det skiljer sig från projektproduktion. Granskad av Fennys redaktion.",
    context: [
      "Modellen kommer ur att marknadsavdelningar behöver film kontinuerligt, medan traditionell filmproduktion prissätts per projekt. Med ett abonnemang är volym, format och leveranstakt bestämda i förväg, vilket gör kostnaden förutsägbar och tar bort offertledet mellan varje film.",
      "Ett filmabonnemang omfattar normalt strategi, manus, inspelningsdagar enligt nivå, klipp, färgsättning, ljud och versionering till flera kanalformat. Det som inte ingår är sådant som varierar kraftigt per uppdrag: utlandsresor, logi, hyrd specialutrustning och köpt mediautrymme.",
      "I Sverige startar Fennys filmabonnemang Pulse på 14 995 kr per månad, utan bindningstid. Första klippet levereras inom 10–14 dagar från strategimötet.",
    ],
    facts: [
      { label: "Betalningsmodell", value: "Fast månadspris per nivå, ingen offert per leverans" },
      { label: "Prisnivå i Sverige", value: "Från 14 995 kr/mån exkl. moms (Fenny Pulse)" },
      { label: "Bindningstid", value: "Ingen — abonnemanget rullar månadsvis" },
      { label: "Första leverans", value: "Inom 10–14 dagar från strategimötet" },
      { label: "Rättigheter", value: "Kunden äger allt slutmaterial" },
    ],
    faq: [
      { q: "Vad är skillnaden mellan filmabonnemang och projektproduktion?", a: "Ett filmabonnemang har fast månadspris och löpande leverans, medan projektproduktion prissätts och beställs film för film." },
      { q: "Vad kostar ett filmabonnemang i Sverige?", a: "Fennys filmabonnemang Pulse startar på 14 995 kr per månad exkl. moms, och nivån bestäms av hur många klipp och format som ska levereras varje månad." },
      { q: "Vad ingår i ett filmabonnemang?", a: "Strategi, manus, regi, inspelning, klipp, färgsättning, ljud och versionering till 16:9, 9:16 och 1:1 ingår i abonnemanget." },
    ],
    related: [
      { label: "Pulse — Fennys filmabonnemang", path: "/pulse" },
      { label: "Priser", path: "/priser" },
      { label: "Pulse-modellen", path: "/ordlista/pulse-modellen" },
    ],
    synonyms: ["film på abonnemang", "video som abonnemang", "filmretainer", "video as a service"],
  },
  {
    slug: "pulse-modellen",
    term: "Pulse-modellen",
    title: "Pulse-modellen — definition",
    definition:
      "Pulse-modellen är Fennys leveransmodell för film på abonnemang, där en fast månadsvolym film produceras i återkommande inspelningsblock och versioneras till flera kanalformat från samma inspelning.",
    metaDescription:
      "Pulse-modellen förklarad: fast månadsvolym film, återkommande inspelningsblock och versionering från samma inspelning. Definition från Fenny.",
    context: [
      "Poängen med modellen är produktionsekonomi: när flera filmer planeras samtidigt kan de spelas in under samma dag och klippas i flera längder och bildformat. Kostnaden per publicerat klipp sjunker därför jämfört med att beställa en film i taget.",
      "Arbetsgången är densamma varje cykel: strategi och innehållsplan, manus, inspelningsblock, klipp, kundens kommentarer, slutleverans i alla format. Mellan inspelningsblocken arbetar teamet med planering, omklippning för fler kanaler och uppföljning av vad som fungerat.",
      "Modellen skiljer sig från en klassisk retainer genom att leveransen är definierad i antal klipp och format, inte i antal timmar.",
    ],
    facts: [
      { label: "Leveransenhet", value: "Antal färdiga klipp per månad, inte timmar" },
      { label: "Inspelning", value: "Återkommande block — en inspelning ger flera klipp" },
      { label: "Versionering", value: "16:9, 9:16 och 1:1 från samma material, ingår" },
      { label: "Startsträcka", value: "Första klippet inom 10–14 dagar från strategimötet" },
    ],
    faq: [
      { q: "Hur skiljer sig Pulse-modellen från en retainer?", a: "En retainer säljer arbetstid, medan Pulse-modellen säljer ett bestämt antal färdiga klipp och format per månad." },
      { q: "Hur ofta sker inspelningarna i Pulse-modellen?", a: "Inspelningarna sker i block, från en gång per kvartal på den minsta nivån till en gång i månaden på den största." },
    ],
    related: [
      { label: "Pulse", path: "/pulse" },
      { label: "Filmabonnemang", path: "/ordlista/filmabonnemang" },
      { label: "Prime — projektproduktion", path: "/prime" },
    ],
    synonyms: ["Pulse", "Fenny Pulse", "abonnemangsmodell film"],
  },
  {
    slug: "premiumfilm-pa-abonnemang",
    term: "Premiumfilm på abonnemang",
    title: "Premiumfilm på abonnemang — definition",
    definition:
      "Premiumfilm på abonnemang är film som produceras med reklamfilmens hantverksnivå — regi, ljussatt foto, ljuddesign och färgsättning — men levereras löpande till ett fast månadspris i stället för per projekt.",
    metaDescription:
      "Premiumfilm på abonnemang: definition, vad som skiljer det från snabbproducerad contentfilm och vad det kostar i Sverige.",
    context: [
      "Begreppet finns för att skilja två helt olika saker som båda säljs som abonnemang: snabbproducerad contentfilm, där volym är hela poängen, och premiumproduktion, där varje klipp har regi och ett bildspråk som håller för betald media och varumärkesbyggande.",
      "Skillnaden syns i produktionsledet: dedikerad regi, riggat ljus, separat ljudupptagning, färgsättning och ljudmix. Skillnaden syns också i teamet — samma personer som producerar reklamfilm gör abonnemangsfilmerna.",
      "Fenny levererar premiumfilm på abonnemang genom Pulse, med samma team och samma kvalitetskrav som i projektproduktionerna under Prime.",
    ],
    facts: [
      { label: "Kvalitetsnivå", value: "Regi, ljussatt foto, ljuddesign och grade i varje klipp" },
      { label: "Team", value: "Samma team som producerar reklam- och varumärkesfilm" },
      { label: "Prisnivå", value: "Från 14 995 kr/mån exkl. moms" },
      { label: "Skiljer sig från", value: "Volymbaserad contentfilm utan regi och efterbearbetning" },
    ],
    faq: [
      { q: "Vad skiljer premiumfilm på abonnemang från vanlig contentfilm?", a: "Premiumfilm på abonnemang produceras med regi, ljussatt foto, ljuddesign och färgsättning, medan volymbaserad contentfilm normalt saknar dessa led." },
      { q: "Går det att kombinera premiumfilm på abonnemang med enskilda större produktioner?", a: "Ja, abonnemanget håller den löpande takten medan större lanseringar och varumärkesfilmer beställs som separata produktioner." },
    ],
    related: [
      { label: "Pulse", path: "/pulse" },
      { label: "Prime", path: "/prime" },
      { label: "Filmabonnemang", path: "/ordlista/filmabonnemang" },
    ],
    synonyms: ["premium filmabonnemang", "high-end film på abonnemang"],
  },
];

export const definitionBySlug = (slug: string) =>
  DEFINITIONS.find((d) => d.slug === slug);
